/**
 * Dentis Promo Status block hydration.
 *
 * Listens for `dentis:discount-resolved` and:
 *   - If a code is active and the current product is eligible (or block is set
 *     to render even when ineligible), reveals the block, replaces placeholders
 *     `{code}`, `{savings_percent}`, `{savings_amount}`, `{shipping_benefit}`.
 *   - If the active Eevy campaign does not re-price the bundle tier being viewed,
 *     switches to the uncovered-tier template (`{code}`, `{tiers}`) and makes no
 *     numeric claim at all — see eevyUncoveredTier.
 *   - Otherwise, leaves the block hidden.
 */
(function () {
  'use strict';

  var EVENT_NAME = 'dentis:discount-resolved';
  var DBG = ((typeof window !== 'undefined' && window.DentisDebug) || { enabled: false, log: function () {}, warn: function () {}, error: function () {} });

  function withThousands(intStr, sep) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    var amount = Number(cents) / 100;
    if (Math.abs(amount - Math.round(amount)) < 0.005) {
      return withThousands(String(Math.round(amount)), ' ') + ' kr';
    }
    return withThousands(String(Math.floor(amount)), ' ') + ',' + (amount.toFixed(2).split('.')[1]) + ' kr';
  }

  // selectedBundleTierKey: the tier the shopper currently has selected in the
  // bundle picker. Scoped to the engine root that owns the block so the
  // announcement and the bundle saving always read the same picker. Falls back
  // to the engine's default tier — that is the tier whose price the picker
  // displays before anything is clicked. Empty string when no picker is on the
  // page, which is what keeps every tier-aware path off non-bundle pages.
  function selectedBundleTierKey(block) {
    try {
      var root = bundleEngineRootForBlock(block);
      if (!root) return '';
      var sectionId = (root.getAttribute && root.getAttribute('data-section')) || '';
      var checked = root.querySelector('input[name="dentis-bundle-' + sectionId + '"]:checked');
      if (checked) return checked.value || checked.getAttribute('value') || '';
      return root.getAttribute('data-be-default-tier') || '';
    } catch (e) { return ''; }
  }

  // eevyCampaignTiers: what the active Eevy bundle campaign does to EVERY tier,
  // resolved through the sovereign Eevy runtime (base display price -> campaign
  // effective price, per tier) — the same source the picker prices from.
  //
  //   tierKey     the tier the shopper is looking at ('' when no picker)
  //   selected    that tier's delta, or null when the campaign leaves its price alone
  //   best        the largest per-tier delta — the campaign's headline number
  //   discounted  every tier the campaign actually re-prices, in picker order
  //
  // Returns null when the resolved is not an Eevy bundle campaign, or when the
  // runtime/base config needed to answer truthfully is not on the page yet.
  function eevyCampaignTiers(resolved, block) {
    try {
      if (!resolved) return null;
      var key = resolved.eevy_campaign_key || null;
      var subs = (Array.isArray(resolved.discounts) && resolved.discounts.length > 0)
        ? resolved.discounts : [];
      if (!key) {
        for (var i = 0; i < subs.length; i++) {
          if (subs[i] && subs[i].eevy_campaign_key) { key = subs[i].eevy_campaign_key; break; }
        }
      }
      var isEevy = resolved.eevy_bundle === true
        || subs.some(function (s) { return s && s.eevy_bundle === true; });
      if (!key && !isEevy) return null;

      var rt = window.Eevy && window.Eevy.bundles;
      var base = window.EevyBundleConfigBase;
      if (!rt || typeof rt.buildEffectiveBundleConfig !== 'function' || !base || !base.campaigns) return null;

      var campaign = (key && base.campaigns[key]) || null;
      if (!campaign && typeof rt.resolveActiveCampaign === 'function') {
        var cands = [];
        if (resolved.code) cands.push(resolved.code);
        subs.forEach(function (s) { if (s && s.code) cands.push(s.code); });
        var match = rt.resolveActiveCampaign(base.campaigns, { candidates: cands, nowMs: Date.now() });
        campaign = match && match.campaign;
      }
      if (!campaign) return null;

      var eff = rt.buildEffectiveBundleConfig(base, campaign);
      var baseTiers = base.tiers || {};
      var effTiers = (eff && eff.tiers) || {};
      function deltaFor(k) {
        var b = Number(baseTiers[k] && baseTiers[k].display_price_cents);
        var e = Number(effTiers[k] && effTiers[k].display_price_cents);
        if (!isFinite(b) || b <= 0 || !isFinite(e) || e >= b) return null;
        return { pct: Math.round((b - e) / b * 100), amountCents: b - e };
      }

      var tierKey = selectedBundleTierKey(block);
      var best = null;
      var discounted = [];
      for (var k in baseTiers) {
        if (!Object.prototype.hasOwnProperty.call(baseTiers, k)) continue;
        var d = deltaFor(k);
        if (!d) continue;
        discounted.push({
          key: k,
          label: (baseTiers[k] && baseTiers[k].label) || k,
          order: Number(baseTiers[k] && baseTiers[k].order) || 0
        });
        if (!best || d.pct > best.pct) best = d;
      }
      discounted.sort(function (a, b) { return a.order - b.order; });

      return {
        tierKey: tierKey,
        selected: tierKey ? deltaFor(tierKey) : null,
        best: best,
        discounted: discounted
      };
    } catch (e) { DBG.warn && DBG.warn('eevyCampaignTiers threw', e); return null; }
  }

  // eevyBundleSavings: the savings an active Eevy bundle campaign delivers,
  // expressed as { pct, amountCents } against the bundle's BASE display price
  // (not the displayed/already-discounted price — that would double count). This
  // is the native re-expression of the old double-discount guard: instead of
  // suppressing the % entirely (the "no proper % value" bug), it derives the
  // campaign's effective discount the same way a native code shows "spart X%".
  //
  // Prefers the tier being viewed; falls back to the largest per-tier delta so a
  // page that cannot name a tier still shows a meaningful headline number. That
  // fallback is only safe once eevyUncoveredTier has ruled out the case where the
  // viewed tier is deliberately excluded — there, the largest delta belongs to a
  // different tier and claiming it is the bug this pair exists to prevent.
  function eevyBundleSavings(resolved, block) {
    var tiers = eevyCampaignTiers(resolved, block);
    if (!tiers) return null;
    return tiers.selected || tiers.best || null;
  }

  // eevyCampaignAwaitingRuntime: the resolved is a display_covered Eevy
  // campaign the published bundle config does not reflect yet (the Eevy runtime
  // has not loaded, or has not resolved this campaign). Every number reachable
  // then comes from the base offer, not the campaign, so the block waits for
  // eevy:bundle-config-resolved.
  function eevyCampaignAwaitingRuntime(resolved) {
    if (!resolved) return false;
    var key = resolved.display_covered === true ? resolved.eevy_campaign_key : null;
    var subs = Array.isArray(resolved.discounts) ? resolved.discounts : [];
    for (var i = 0; !key && i < subs.length; i++) {
      if (subs[i] && subs[i].display_covered === true && subs[i].eevy_campaign_key) key = subs[i].eevy_campaign_key;
    }
    if (!key) return false;
    var published = window.EevyBundleResolved;
    return !(published && published.campaignKey === key);
  }

  // eevyUncoveredTier: the shopper is looking at a tier this campaign does not
  // re-price, while it does re-price others. This is the single predicate that
  // decides the announcement makes no numeric claim, because EVERY number
  // reachable from here is borrowed from another tier — the largest per-tier
  // delta, the headline percent on the resolved, the composed stack, and the
  // compare-at bundle saving. They can only be closed together.
  //
  // Returns null — today's paths, untouched — in the two cases that look similar
  // and are not:
  //   - no tier is determinable (a page with no bundle picker): nothing is being
  //     claimed about a tier, so there is nothing to correct
  //   - the campaign re-prices NO tier at all (message / component-only): there
  //     is no other tier to point at, and its authored copy is the right answer
  function eevyUncoveredTier(resolved, block) {
    var tiers = eevyCampaignTiers(resolved, block);
    if (!tiers) return null;
    if (!tiers.tierKey || tiers.selected) return null;
    if (!tiers.discounted.length) return null;
    return tiers;
  }

  // commonSuffixLength: how many trailing characters every label shares
  // (case-insensitive). Never consumes a whole label, so a label that is itself
  // the suffix of another can't collapse to nothing.
  function commonSuffixLength(labels) {
    var min = labels[0].length;
    for (var i = 1; i < labels.length; i++) min = Math.min(min, labels[i].length);
    var n = 0;
    while (n < min - 1) {
      var ch = labels[0].charAt(labels[0].length - 1 - n).toLowerCase();
      for (var j = 1; j < labels.length; j++) {
        if (labels[j].charAt(labels[j].length - 1 - n).toLowerCase() !== ch) return n;
      }
      n++;
    }
    return n;
  }

  // tierPhrase: name the tiers a campaign discounts, inside a sentence. Nordic
  // and Germanic markets collapse a shared trailing noun onto a hyphen —
  // "Startpakken og Duopakken" is written "start- og duopakken" — so a plain join
  // reads as translated-from-English. Collapse only when the labels genuinely
  // share that trailing noun; anything else joins plainly rather than inventing
  // morphology on a label this code cannot parse. The conjunction is the market's
  // word and arrives from Liquid, so no language lives in this file.
  function tierPhrase(entries, conjunction) {
    var labels = [];
    for (var i = 0; i < (entries || []).length; i++) {
      var raw = String((entries[i] && entries[i].label) || '').trim();
      if (raw) labels.push(raw.charAt(0).toLowerCase() + raw.slice(1));
    }
    if (!labels.length) return '';
    if (labels.length === 1) return labels[0];

    var last = labels[labels.length - 1];
    var heads = labels.slice(0, -1);
    var joiner = conjunction ? ' ' + conjunction + ' ' : ', ';
    var n = conjunction ? commonSuffixLength(labels) : 0;
    if (n >= 3) {
      var clipped = [];
      for (var j = 0; j < heads.length; j++) {
        var head = heads[j].slice(0, heads[j].length - n);
        if (head.length < 2) { clipped = null; break; }
        clipped.push(head + '-');
      }
      if (clipped) return clipped.join(', ') + joiner + last;
    }
    return heads.join(', ') + joiner + last;
  }

  function getProductEligibility(resolved, productId) {
    if (!resolved || !resolved.active) return null;
    if (resolved.type !== 'percentage' && resolved.type !== 'fixed') return null;
    var bucket = resolved.eligible && resolved.eligible[String(productId || '')];
    if (bucket) {
      var perVariant = 0;
      for (var k in bucket.variants) {
        if (Number(bucket.variants[k]) > perVariant) perVariant = Number(bucket.variants[k]);
      }
      return {
        total: Number(bucket.savings_total || 0),
        perVariant: perVariant
      };
    }
    // Heuristic: target_selection === 'all' codes apply to every product.
    if (resolved.target_selection === 'all' && (resolved.target_type === 'line_item' || !resolved.target_type)) {
      return { total: 0, perVariant: 0, fromAllSelector: true };
    }
    return null;
  }

  function computeAmountCents(resolved, currentPriceCents, perVariantSavingsCents) {
    if (!resolved) return 0;
    // Prefer formula-derived savings tied to the currently displayed variant
    // so the announcement always matches what the bundle picker shows. Falls
    // back to the engine's per-variant cart-derived savings only if no
    // current variant price is available (defensive — block always renders
    // data-dentis-current-price from Liquid).
    if (resolved.type === 'percentage' && Number(resolved.value) > 0 && currentPriceCents > 0) {
      return Math.round(Number(currentPriceCents) * Number(resolved.value) / 100);
    }
    if (resolved.type === 'fixed' && Number(resolved.value) > 0) {
      return Math.round(Number(resolved.value) * 100);
    }
    return Number(perVariantSavingsCents || 0);
  }

  // Compose cumulative savings + active code list across all kept subs in
  // resolved.discounts. The announcement is a product-level summary, so it
  // lists ALL kept sub codes from the engine — not just those the composer
  // matched for one specific variant. The composer is only used for the
  // cumulative {savings_amount} / {savings_percent}, with a best-effort
  // variant fallback so cores that restrict_to_variants still contribute
  // when no specific variant id is passed in.
  function composeStack(resolved, productId, basePriceCents, variantId) {
    var subs = (resolved && Array.isArray(resolved.discounts) && resolved.discounts.length > 0)
      ? resolved.discounts
      : (resolved ? [resolved] : []);
    if (subs.length === 0) return { codes: [], percentEffective: 0, amountCents: 0 };

    var allCodes = subs.map(function (s) { return s.code || ''; }).filter(function (c) { return !!c; });

    var po = window.DentisPriceOverride;
    if (po && typeof po.compose === 'function' && basePriceCents > 0) {
      // Prefer the caller-provided variant id. If absent, sniff any variant
      // id out of the subs' eligible buckets so cores tied to specific
      // variants still get composed in the cumulative math.
      var vid = String(variantId || '');
      if (!vid) {
        for (var si = 0; si < subs.length; si++) {
          var bucket = subs[si].eligible && subs[si].eligible[String(productId || '')];
          if (bucket && bucket.variants) {
            var keys = Object.keys(bucket.variants);
            if (keys.length > 0) { vid = keys[0]; break; }
          }
        }
      }
      var composed = po.compose(resolved, basePriceCents, productId, vid);
      if (composed && composed.applied && composed.applied.length > 0) {
        var pct = basePriceCents > 0 ? Math.round((composed.savingsCents / basePriceCents) * 100) : 0;
        return { codes: allCodes, percentEffective: pct, amountCents: composed.savingsCents };
      }
    }
    // Composer unavailable or didn't match anything — still surface all codes
    // and the primary's percent so the announcement stays meaningful.
    var primary = subs[0] || {};
    var primaryPct = primary.type === 'percentage' ? Math.round(Number(primary.value || 0)) : 0;
    return { codes: allCodes, percentEffective: primaryPct, amountCents: 0 };
  }

  // Authored Eevy campaign copy on the resolved payload (message / spart_message
  // / badge). Used when no numeric savings is derivable so message-only and
  // component-level campaigns still show a useful announcement.
  function eevyAuthoredCopy(resolved) {
    if (!resolved) return null;
    var msg = resolved.message != null && String(resolved.message).trim()
      ? String(resolved.message).trim()
      : null;
    var spart = resolved.spart_message != null && String(resolved.spart_message).trim()
      ? String(resolved.spart_message).trim()
      : null;
    if (msg || spart) return { message: msg, spart_message: spart || msg };
    var subs = Array.isArray(resolved.discounts) ? resolved.discounts : [];
    for (var i = 0; i < subs.length; i++) {
      var s = subs[i];
      if (!s) continue;
      msg = s.message != null && String(s.message).trim() ? String(s.message).trim() : null;
      spart = s.spart_message != null && String(s.spart_message).trim()
        ? String(s.spart_message).trim()
        : null;
      if (msg || spart) return { message: msg, spart_message: spart || msg };
    }
    return null;
  }

  function fillTemplate(template, resolved, perVariantSavingsCents, currentPriceCents, productId, variantId, options) {
    var multiList = !!(options && options.multiList);
    var code = (resolved && resolved.code) || '';

    // A tier the active campaign does not re-price. Every number reachable below
    // this point belongs to a different tier — the per-tier delta, the headline
    // percent, the composed stack and the compare-at bundle override — so
    // returning here is what closes them together instead of one at a time.
    // Savings placeholders still resolve, to empty, so authored copy that carries
    // one can never print a borrowed number.
    var uncovered = options && options.uncovered;
    if (uncovered) {
      var uncoveredTemplate = (options && options.uncoveredTemplate) || '';
      if (!uncoveredTemplate) return '';
      return uncoveredTemplate
        .replace(/\{codes\}/g, code)
        .replace(/\{code\}/g, code)
        .replace(/\{tiers\}/g, tierPhrase(uncovered.discounted, options && options.tierConjunction))
        .replace(/\{savings_percent\}/g, '')
        .replace(/\{savings_amount\}/g, '')
        .replace(/\{shipping_benefit\}/g, '');
    }

    if (!template) return '';
    var pct = '';
    var amt = '';
    var shipping = '';
    var codesJoined = code;

    // The placeholder the merchant picks ({savings_percent} vs
    // {savings_amount}) is only an output format - it must render a correct
    // value no matter what type the discount itself is. So derive BOTH the
    // amount and the percent from the same underlying savings, rather than
    // gating each format on a matching discount type. computeAmountCents
    // already normalizes percentage codes (base * pct) and fixed codes
    // (value) into a savings amount in cents.
    //
    // Eevy bundle campaign (display_covered): its savings are already baked into
    // the displayed bundle offer price, so multiplying the displayed price by
    // the % here would double-count. Instead derive the % + amount from the
    // bundle's BASE -> campaign effective price for the selected tier — the same
    // "spart X%" a native code shows. Falls back to the headline % (resolved.value)
    // when the runtime can't resolve a tier delta. When still no number exists,
    // the processBlock path prefers authored campaign.message / spart_message.
    var eevySavings = eevyBundleSavings(resolved, options && options.block);
    if (eevySavings) {
      pct = String(eevySavings.pct);
      if (eevySavings.amountCents > 0) amt = formatMoney(eevySavings.amountCents);
    } else if (resolved && resolved.display_covered === true) {
      // Eevy campaign but no resolvable tier delta — fall back to the headline
      // percent carried on the resolved (still avoids double-counting the price).
      if (Number(resolved.value) > 0) pct = String(Math.round(Number(resolved.value)));
    } else {
      var amountCents = computeAmountCents(resolved, currentPriceCents, perVariantSavingsCents);
      if (amountCents > 0) {
        amt = formatMoney(amountCents);
      }

      if (resolved && resolved.type === 'percentage' && Number(resolved.value) > 0) {
        // Authoritative percent straight from the code definition.
        pct = String(Math.round(Number(resolved.value)));
      } else if (amountCents > 0 && currentPriceCents > 0) {
        // Fixed-amount (or otherwise) code: compute the effective percent from
        // the actual savings against the current variant price.
        pct = String(Math.round(amountCents / currentPriceCents * 100));
      }
    }

    if (resolved && resolved.type === 'shipping') {
      shipping = 'gratis frakt';
    }

    // Multi-sub awareness: when more than one sub is present, the {codes}
    // placeholder joins all kept codes and the cumulative savings amount /
    // effective percent comes from the matrix composer. {code} stays as
    // the primary for back-compat.
    //
    // multiList=true switches `{codes}` rendering to a newline-separated
    // bulleted list ("- CODE_A\n- CODE_B") so the block can show stacked
    // codes on individual lines via the dedicated multi-code template. The
    // surrounding element needs `white-space: pre-line` for the line breaks
    // to render — handled by the `dentis-promo-status--multi` modifier
    // class toggled in processBlock.
    var subs = (resolved && Array.isArray(resolved.discounts)) ? resolved.discounts : [];
    if (subs.length > 1) {
      var stack = composeStack(resolved, productId, currentPriceCents, variantId);
      if (stack.codes.length > 0) {
        codesJoined = multiList
          ? stack.codes.map(function (c) { return '- ' + c; }).join('\n')
          : stack.codes.join(' + ');
      }
      // Skip composer-derived cumulative savings when an Eevy bundle campaign
      // already reflects its savings in the displayed bundle price
      // (double-discount guard) — eevyBundleSavings above already set the
      // correct tier %/amount. Codes still list above; only the composer's
      // stacked savings number is suppressed for the covered case.
      if (!eevySavings && !(resolved && resolved.display_covered === true)) {
        if (stack.amountCents > 0) amt = formatMoney(stack.amountCents);
        if (stack.percentEffective > 0) pct = String(stack.percentEffective);
      }
    }

    // Eevy bundle/campaign reconciliation (authoritative). When the page has
    // an eevy bundle engine, the true saving comes from the bundle data
    // objects (selected tier compare-at total vs its order-discounted display
    // price), the exact source the bundle picker and headline price use. This
    // overrides the raw discount-code percentage so the announcement agrees
    // with the picker (e.g. 40% / 50% instead of the code's 29% / 25%).
    var bundleSaving = options && options.bundleSaving;
    if (bundleSaving && bundleSaving.percent > 0) {
      pct = String(bundleSaving.percent);
      if (bundleSaving.savedCents > 0) amt = formatMoney(bundleSaving.savedCents);
    }

    // Message-only / component-level Eevy campaigns: when the template would
    // leave savings placeholders empty (e.g. "du har spart %"), prefer the
    // authored campaign message so the announcement is still useful.
    var isEevy = !!(resolved && (resolved.eevy_bundle === true || resolved.display_covered === true
      || (Array.isArray(resolved.discounts) && resolved.discounts.some(function (s) {
        return s && (s.eevy_bundle === true || s.display_covered === true);
      }))));
    if (isEevy && !pct && !amt) {
      var authored = eevyAuthoredCopy(resolved);
      if (authored && authored.message) return authored.message;
      if (authored && authored.spart_message) return authored.spart_message;
    }

    return template
      .replace(/\{codes\}/g, codesJoined)
      .replace(/\{code\}/g, code)
      .replace(/\{savings_percent\}/g, pct)
      .replace(/\{savings_amount\}/g, amt)
      .replace(/\{shipping_benefit\}/g, shipping);
  }

  // Resolve the active template + multi-list flag for a block based on how
  // many kept sub-codes the resolved state actually has. When multiple subs
  // are present AND the block has a non-empty `data-dentis-template-multi`,
  // use that template with multiList rendering. Otherwise fall back to the
  // single-code template.
  function selectTemplate(block, resolved) {
    var single = block.getAttribute('data-dentis-template') || '';
    var multi = block.getAttribute('data-dentis-template-multi') || '';
    var subs = (resolved && Array.isArray(resolved.discounts)) ? resolved.discounts : [];
    if (subs.length > 1 && multi.trim() !== '') {
      return { template: multi, multiList: true };
    }
    return { template: single, multiList: false };
  }

  function readCurrentPrice(block) {
    var raw = block.getAttribute('data-dentis-current-price')
           || block.getAttribute('data-dentis-fallback-price')
           || '0';
    var parsed = parseInt(raw, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  function processBlock(block, resolved) {
    var productId = block.getAttribute('data-dentis-product-id');
    var variantId = block.getAttribute('data-dentis-current-variant-id') || '';
    var textTarget = block.querySelector('[data-dentis-promo-text]');
    var currentPrice = readCurrentPrice(block);
    DBG.log('processBlock', { block: block, productId: productId, hasTextTarget: !!textTarget, currentPrice: currentPrice, resolved: resolved });
    if (!textTarget) {
      DBG.warn('promo block has no [data-dentis-promo-text] child; skipping');
      return;
    }

    if (!resolved || !resolved.active) {
      DBG.log('no active discount; hiding promo block');
      block.setAttribute('hidden', '');
      return;
    }

    if (eevyCampaignAwaitingRuntime(resolved)) {
      DBG.log('Eevy campaign not yet in the published bundle config; hiding promo block');
      block.setAttribute('hidden', '');
      return;
    }

    var picked = selectTemplate(block, resolved);
    if (picked.multiList) {
      block.classList.add('dentis-promo-status--multi');
    } else {
      block.classList.remove('dentis-promo-status--multi');
    }
    var opts = {
      multiList: picked.multiList,
      block: block,
      bundleSaving: selectedBundleSaving(resolved, block),
      uncovered: eevyUncoveredTier(resolved, block),
      uncoveredTemplate: block.getAttribute('data-dentis-template-uncovered') || '',
      tierConjunction: block.getAttribute('data-dentis-tier-conjunction') || ''
    };

    // paint: render and reveal. On an uncovered tier an empty result means the
    // merchant cleared the copy for that case — hide rather than announce a code
    // with nothing true left to say about the tier being viewed.
    function paint(perVariantSavingsCents) {
      var text = fillTemplate(picked.template, resolved, perVariantSavingsCents || 0, currentPrice, productId, variantId, opts);
      if (opts.uncovered && !text) {
        block.setAttribute('hidden', '');
        return;
      }
      textTarget.textContent = text;
      block.removeAttribute('hidden');
    }

    if (resolved.type === 'shipping') {
      paint(0);
      return;
    }

    if (resolved.type !== 'percentage' && resolved.type !== 'fixed') {
      // BXGY etc. — show static "code active" status using the active template
      paint(0);
      return;
    }

    var elig = getProductEligibility(resolved, productId);
    var suppressWhenIneligible = block.getAttribute('data-dentis-suppress-when-ineligible') === '1';
    if (!elig) {
      if (suppressWhenIneligible) {
        block.setAttribute('hidden', '');
        return;
      }
      // Render generic active text without product-specific savings
      paint(0);
      return;
    }

    paint(elig.perVariant);
  }

  function processAll(resolved) {
    var blocks = document.querySelectorAll('[data-dentis-promo-status]');
    DBG.log('processAll: found ' + blocks.length + ' [data-dentis-promo-status] blocks');
    for (var i = 0; i < blocks.length; i++) processBlock(blocks[i], resolved);
  }

  function normalizeBundleOrderDiscounts(resolved) {
    if (!resolved || !resolved.active) return [];
    var subs = (Array.isArray(resolved.discounts) && resolved.discounts.length > 0)
      ? resolved.discounts
      : [resolved];
    return subs.filter(function (sub) {
      if (!sub || sub.active === false) return false;
      if (sub.kind !== 'order') return false;
      if (sub.type !== 'percentage' && sub.value_type !== 'percentage') return false;
      var value = Number(sub.value);
      return isFinite(value) && value > 0 && value < 100;
    }).map(function (sub) {
      return { code: sub.code || '', value: Number(sub.value) };
    });
  }

  function bundleDisplayPriceCents(basePriceCents, orderDiscounts) {
    var price = Number(basePriceCents) || 0;
    if (!price || !orderDiscounts.length) return price;
    for (var i = 0; i < orderDiscounts.length; i++) {
      var pct = Number(orderDiscounts[i].value) || 0;
      if (pct > 0 && pct < 100) price = Math.max(0, Math.round(price * (100 - pct) / 100));
    }
    return price;
  }

  function formatBundleCents(cents) {
    return Math.round((Number(cents) || 0) / 100).toString() + ' kr';
  }

  function bundlePctSaved(price, compare) {
    price = Number(price) || 0;
    compare = Number(compare) || 0;
    if (!compare || compare <= price) return 0;
    return Math.round(((compare - price) / compare) * 100);
  }

  function bundleTierSelectionKey(root, tierKey, tier) {
    var sectionId = root.getAttribute('data-section') || '';
    if (tier && tier.select === 'combo') {
      var duo = root.querySelector('input[name="dentis-duocolor-' + sectionId + '"]:checked');
      return duo ? duo.value : '';
    }
    var color = root.querySelector('input[name="dentis-color-' + sectionId + '"]:checked');
    return color ? color.value : '';
  }

  function bundleComponentsFor(config, root, tierKey) {
    var tier = config && config.tiers && config.tiers[tierKey];
    if (!tier || !tier.components_by_selection) return [];
    var selection = bundleTierSelectionKey(root, tierKey, tier);
    var bySelection = tier.components_by_selection;
    return bySelection[selection] || bySelection[Object.keys(bySelection)[0]] || [];
  }

  function bundleCompareCentsFor(config, root, tierKey) {
    var total = 0;
    var items = (config && config.items) || {};
    var comps = bundleComponentsFor(config, root, tierKey);
    for (var i = 0; i < comps.length; i++) {
      var comp = comps[i] || {};
      var item = items[comp.item];
      if (!item) continue;
      var unit = (item.compare_at_cents != null ? item.compare_at_cents : item.unit_price_cents) || 0;
      total += unit * (comp.qty || 1);
    }
    return total;
  }

  function getBundleConfig() {
    var config = window.EevyBundleConfig;
    if (typeof config === 'string') {
      try { config = JSON.parse(config); } catch (e) { return null; }
    }
    if (!config || !config.tiers) return null;
    return config;
  }

  // The engine root that owns the promo block. Scope to the block's own
  // section when possible (multiple bundle engines could coexist on one
  // page); otherwise fall back to the first engine on the page.
  function bundleEngineRootForBlock(block) {
    var roots = document.querySelectorAll('[data-be-engine]');
    if (!roots.length) return null;
    if (block && block.closest) {
      var section = block.closest('.shopify-section') || block.closest('[data-section]');
      if (section && section.contains) {
        for (var i = 0; i < roots.length; i++) {
          if (section.contains(roots[i])) return roots[i];
        }
      }
    }
    return roots[0];
  }

  // Authoritative saving for eevy-managed bundle/campaign discounts. Reads the
  // currently selected tier straight from the eevy bundle data objects and
  // returns the SAME compare-vs-display saving the bundle picker and headline
  // price already render. This reconciles the announcement's "du har spart X%"
  // with the true bundle saving (e.g. 40% / 50%) instead of the raw discount
  // code percentage (e.g. 29% / 25%), which only reflects the order-level cut
  // applied on top of the bundle's display price.
  //
  // Non-bundle pages (no EevyBundleConfig / no [data-be-engine]) return null
  // and leave the existing code-derived savings path untouched.
  function selectedBundleSaving(resolved, block) {
    var config = getBundleConfig();
    if (!config) return null;
    var root = bundleEngineRootForBlock(block);
    if (!root) return null;
    var sectionId = root.getAttribute('data-section') || '';
    var checkedTier = root.querySelector('input[name="dentis-bundle-' + sectionId + '"]:checked');
    var tierKey = checkedTier ? checkedTier.value : (root.getAttribute('data-be-default-tier') || '');
    if (!tierKey) return null;
    var tier = config.tiers[tierKey];
    if (!tier || tier.display_price_cents == null) return null;
    var orderDiscounts = normalizeBundleOrderDiscounts(resolved);
    var price = bundleDisplayPriceCents(tier.display_price_cents, orderDiscounts);
    var compare = bundleCompareCentsFor(config, root, tierKey);
    if (!(compare > price && compare > 0)) return null;
    return {
      percent: bundlePctSaved(price, compare),
      savedCents: compare - price,
      priceCents: price,
      compareCents: compare
    };
  }

  function updateBundleEngineDisplayPrices(resolved) {
    var config = window.EevyBundleConfig;
    if (typeof config === 'string') {
      try { config = JSON.parse(config); } catch (e) { return; }
    }
    if (!config || !config.tiers) return;
    var orderDiscounts = normalizeBundleOrderDiscounts(resolved);
    var roots = document.querySelectorAll('[data-be-engine]');
    for (var r = 0; r < roots.length; r++) {
      var root = roots[r];
      var sectionId = root.getAttribute('data-section') || '';
      var checkedTier = root.querySelector('input[name="dentis-bundle-' + sectionId + '"]:checked');
      var selectedTierKey = checkedTier ? checkedTier.value : '';
      var cards = root.querySelectorAll('[data-be-tier]');

      for (var c = 0; c < cards.length; c++) {
        var card = cards[c];
        var tierKey = card.getAttribute('data-be-tier') || '';
        var tier = config.tiers[tierKey];
        if (!tier || tier.display_price_cents == null) continue;
        var price = bundleDisplayPriceCents(tier.display_price_cents, orderDiscounts);
        var compare = bundleCompareCentsFor(config, root, tierKey);
        var percent = bundlePctSaved(price, compare);
        var savedKr = compare > price ? Math.round((compare - price) / 100) : 0;
        var priceEl = card.querySelector('[data-be-price-current]');
        var compareEl = card.querySelector('[data-be-price-original]');
        var badgeEl = card.querySelector('[data-be-tier-badge]');
        var badgeFormat = card.getAttribute('data-be-badge-format') || '';

        if (priceEl) priceEl.textContent = formatBundleCents(price);
        if (compareEl) {
          if (compare > price && compare > 0) {
            compareEl.textContent = formatBundleCents(compare);
            compareEl.style.display = '';
          } else {
            compareEl.textContent = '';
            compareEl.style.display = 'none';
          }
        }
        if (badgeEl) {
          var hasToken = /\{percent\}|\{saved\}/.test(badgeFormat);
          if (badgeFormat && (!hasToken || percent > 0)) {
            badgeEl.textContent = badgeFormat
              .replace(/\{percent\}/g, String(percent))
              .replace(/\{saved\}/g, String(savedKr));
            badgeEl.style.display = '';
          } else {
            badgeEl.textContent = '';
            badgeEl.style.display = 'none';
          }
        }
      }

      if (selectedTierKey && config.tiers[selectedTierKey]) {
        var selectedBasePrice = Number(config.tiers[selectedTierKey].display_price_cents) || 0;
        var selectedPrice = bundleDisplayPriceCents(selectedBasePrice, orderDiscounts);
        var selectedCompare = bundleCompareCentsFor(config, root, selectedTierKey);
        var priceBlock = document.getElementById('price-' + sectionId);
        if (priceBlock) {
          var regularParent = priceBlock.querySelector('.price__regular');
          var saleParent = priceBlock.querySelector('.price__sale');
          var noSale = priceBlock.querySelector('.price__regular .price-item--regular');
          var beforeSale = priceBlock.querySelector('.price__sale .price-item--regular');
          var saleSpan = priceBlock.querySelector('.price__sale .price-item--sale');
          var badge = priceBlock.querySelector('.price__badge-sale');
          if (selectedCompare > selectedPrice && selectedCompare > 0) {
            if (regularParent) regularParent.style.display = 'none';
            if (saleParent) saleParent.style.display = 'block';
            if (beforeSale) beforeSale.textContent = formatBundleCents(selectedCompare);
            if (saleSpan) {
              saleSpan.textContent = formatBundleCents(selectedPrice);
              saleSpan.style.display = 'inline-block';
            }
            if (badge) {
              badge.textContent = 'Spar ' + bundlePctSaved(selectedPrice, selectedCompare) + '%';
              badge.style.display = 'inline-block';
            }
          } else {
            if (regularParent) regularParent.style.display = 'block';
            if (saleParent) saleParent.style.display = 'none';
            if (noSale) noSale.textContent = formatBundleCents(selectedPrice);
            if (saleSpan) saleSpan.style.display = 'none';
            if (badge) badge.style.display = 'none';
          }
        }
      }
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Cart drawer discount banner — Liquid pre-fills it when Shopify already has
  // the discount in cart.cart_level_discount_applications (order-level codes
  // attach even on an empty cart). Product-level / entitled automatic
  // discounts never land in that array on an empty / non-entitled cart, so
  // this fills the banner from the engine-resolved code instead. The
  // data-dentis-source attribute on the codes wrapper is the conflict guard:
  //   - 'liquid' => Shopify-attached; the JS never overwrites or hides this
  //   - 'js'     => filled by this function; safe to re-render or clear
  //   - absent   => empty placeholder; safe to fill
  function processCartDiscountBanner(resolved) {
    var banners = document.querySelectorAll('[data-dentis-cart-discount-banner]');
    if (!banners.length) return;
    DBG.log('processCartDiscountBanner: found ' + banners.length + ' banner(s)', resolved);
    for (var i = 0; i < banners.length; i++) {
      var banner = banners[i];
      var codesEl = banner.querySelector('[data-dentis-cart-discount-codes]');
      if (!codesEl) continue;

      var source = codesEl.getAttribute('data-dentis-source');
      if (source === 'liquid') {
        // Order-level path: Shopify cart already has the discount. Leave alone.
        banner.removeAttribute('hidden');
        continue;
      }

      if (resolved && resolved.active && resolved.code) {
        codesEl.innerHTML = '<strong class="dentis-cart-discount-banner__code">'
          + escapeHtml(resolved.code) + '</strong>';
        codesEl.setAttribute('data-dentis-source', 'js');
        banner.removeAttribute('hidden');
      } else if (source === 'js') {
        // Previously JS-sourced but discount no longer active — clear & hide.
        codesEl.innerHTML = '';
        codesEl.removeAttribute('data-dentis-source');
        banner.setAttribute('hidden', '');
      } else {
        banner.setAttribute('hidden', '');
      }
    }
  }

  function handle(resolved) {
    try {
      // The active Eevy bundle campaign is a NATIVE discount source: the engine
      // ingests it into DentisStaticPromoConfig.campaigns and resolves it
      // through the same path as every other discount, so this one `resolved`
      // state drives the announcement, cart banner, and bundle-price repaint.
      // The Eevy sub carries display_covered:true — price-override skips
      // painting it (Eevy owns the displayed bundle price) and the announcement
      // derives the % from the bundle offer (eevyBundleSavings) instead.
      processAll(resolved);
      processCartDiscountBanner(resolved);
      updateBundleEngineDisplayPrices(resolved);
    } catch (e) { DBG.error('handle() threw', e); }
  }

  if (typeof document !== 'undefined') {
  document.addEventListener(EVENT_NAME, function (ev) {
    handle((ev && ev.detail) || window.DentisActiveDiscount);
  });

  // After cart-sync re-renders the cart drawer, the banner DOM is fresh.
  // Re-sync the banner from the last known resolved state so a product-level
  // code that isn't in cart_level_discount_applications still shows up.
  document.addEventListener('dentis:cart-drawer:updated', function () {
    try { processCartDiscountBanner(window.DentisActiveDiscount || null); }
    catch (e) { DBG.error('cart-drawer:updated handler threw', e); }
  });

  // Keep {savings_amount} in sync with the bundle picker by re-rendering
  // promo-status blocks whenever the selected variant changes. The bundle
  // picker dispatches `variant:changed` on `document` with `e.detail.variant`
  // (full Shopify variant object — price is in cents).
  document.addEventListener('variant:changed', function (ev) {
    var variant = ev && ev.detail && ev.detail.variant;
    if (!variant) return;
    var nextPrice = Number(variant.price);
    if (!isFinite(nextPrice) || nextPrice <= 0) return;
    var blocks = document.querySelectorAll('[data-dentis-promo-status]');
    for (var i = 0; i < blocks.length; i++) {
      blocks[i].setAttribute('data-dentis-current-price', String(nextPrice));
      if (variant.id) blocks[i].setAttribute('data-dentis-current-variant-id', String(variant.id));
    }
    if (window.DentisActiveDiscount) {
      DBG.log('variant:changed -> re-rendering promo blocks with price', nextPrice);
      handle(window.DentisActiveDiscount);
    }
  });

  document.addEventListener('change', function (ev) {
    var target = ev && ev.target;
    if (!target || !target.closest || !target.closest('[data-be-engine]')) return;
    if (window.DentisActiveDiscount) {
      setTimeout(function () {
        updateBundleEngineDisplayPrices(window.DentisActiveDiscount);
        // Re-render the announcement so its bundle-derived saving tracks the
        // newly selected tier/color (e.g. start 40% -> duo 50%).
        processAll(window.DentisActiveDiscount);
      }, 0);
    }
  });

  // The engine suppresses a dispatch identical to the last one, so when the
  // Eevy runtime publishes the campaign after the engine has dispatched, this
  // is the only signal that the campaign's numbers are now computable.
  document.addEventListener('eevy:bundle-config-resolved', function () {
    if (!window.DentisActiveDiscount) return;
    setTimeout(function () { handle(window.DentisActiveDiscount); }, 0);
  });

  function maybeReplay() {
    if (window.DentisActiveDiscount) {
      DBG.log('replay: window.DentisActiveDiscount already set on listener attach', window.DentisActiveDiscount);
      handle(window.DentisActiveDiscount);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeReplay);
  } else {
    setTimeout(maybeReplay, 0);
  }
  }

  // CommonJS export for the Node test harness. No-op in browsers.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      eevyBundleSavings: eevyBundleSavings,
      eevyCampaignTiers: eevyCampaignTiers,
      eevyUncoveredTier: eevyUncoveredTier,
      eevyCampaignAwaitingRuntime: eevyCampaignAwaitingRuntime,
      tierPhrase: tierPhrase,
      selectedBundleTierKey: selectedBundleTierKey
    };
  }
})();
